import { createEffect, createSignal, mergeProps, onCleanup, splitProps } from 'solid-js';
import type { JSX } from 'cdui-js';
import { Icon } from 'cdui-js';
import './difi-textarea.css';

/** 预留默认值，可按设计稿调整 */
const DEFAULT_ROWS = 4;
const DEFAULT_MAX_LENGTH = 500;
const DEFAULT_PLACEHOLDER = '';
const DEFAULT_CLEAR_TEXT = '清空';
const DEBOUNCEMS = 300;
const SHOW_CLEAR_BTN = true;
const SHOW_COUNT_BTN = true;

const innerBoxStyle: JSX.CSSProperties = {
  position: 'relative',
  width: '100%',
};

const clearBtnStyle: JSX.CSSProperties = {
  position: 'absolute',
  top: '6px',
  right: '8px',
  'z-index': 1,
  border: 'none',
  background: 'transparent',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
};

const countStyle: JSX.CSSProperties = {
  position: 'absolute',
  right: '8px',
  bottom: '6px',
  'z-index': 1,
  'line-height': 1.2,
  'pointer-events': 'none',
};

export type DiFiTextareaProps = {
  /** 受控值 */
  value?: string;
  /** 非受控初始值 */
  defaultValue?: string;
  /**
   * 最大字符数（按 Unicode 标量计数，适合中文）；
   * 未传入时使用 DEFAULT_MAX_LENGTH；设为 0 表示不限制字数（不展示计数）。
   */
  maxLength?: number;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  readonly?: boolean;
  /** 是否展示「清空」 */
  showClear?: boolean;
  /** 是否在右下角展示字数（依赖 maxLength） */
  showCount?: boolean;
  clearText?: string;
  /** 最外层容器 */
  wrapperClass?: string;
  textareaClass?: string;
  textareaStyle?: JSX.CSSProperties;
  /**
   * 内容变化（输入、粘贴、程序化同步后均通过此处回传）。
   * debounceMs 为正整数时对 onUpdateValue 防抖；输入框展示仍实时更新。
   */
  onUpdateValue?: (value: string) => void;
  /** onUpdateValue 防抖毫秒；0 或未传表示不防抖 */
  debounceMs?: number;
  /** 点击清空后触发（先于 onUpdateValue('')） */
  onClear?: () => void;
  class?: string;
  style?: JSX.CSSProperties;
};

//设置默认值
const defaultProps: Required<
  Pick<
    DiFiTextareaProps,
    | 'defaultValue'
    | 'placeholder'
    | 'rows'
    | 'disabled'
    | 'readonly'
    | 'showClear'
    | 'showCount'
    | 'clearText'
    | 'maxLength'
    | 'debounceMs'
  >
> = {
  defaultValue: '',
  placeholder: DEFAULT_PLACEHOLDER,
  rows: DEFAULT_ROWS,
  disabled: false,
  readonly: false,
  showClear: SHOW_CLEAR_BTN,
  showCount: SHOW_COUNT_BTN,
  clearText: DEFAULT_CLEAR_TEXT,
  maxLength: DEFAULT_MAX_LENGTH,
  debounceMs: DEBOUNCEMS,
};

function clampByMaxLength(value: string, max: number): string {
  if (max <= 0) return value;
  const chars = [...value];
  if (chars.length <= max) return value;
  return chars.slice(0, max).join('');
}

function charCount(value: string): number {
  return [...value].length;
}

/**
 * 多行输入：字数上限、清空、变更均通过 onUpdateValue / onClear 回传（对齐 Naive / Element Plus 表单回调习惯）。
 */
export const DiFiTextarea = (props: DiFiTextareaProps) => {
  const merged = mergeProps(defaultProps, props);

  const [local, textareaRest] = splitProps(merged, [
    'value',
    'defaultValue',
    'maxLength',
    'onUpdateValue',
    'debounceMs',
    'onClear',
    'showClear',
    'showCount',
    'clearText',
    'wrapperClass',
    'textareaClass',
    'textareaStyle',
    'class',
    'style',
    'placeholder',
    'rows',
    'disabled',
    'readonly',
  ]);

  const isControlled = () => local.value !== undefined;

  const [live, setLive] = createSignal(
    (local.value !== undefined ? (local.value as string) : local.defaultValue) as string,
  );

  createEffect(() => {
    const v = local.value;
    if (v !== undefined) {
      setLive(() => v as string);
    }
  });

  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  const cancelDebounce = () => {
    if (debounceTimer !== undefined) {
      clearTimeout(debounceTimer);
      debounceTimer = undefined;
    }
  };

  onCleanup(() => cancelDebounce());

  const effectiveMax = () => local.maxLength;

  const emitUpdate = (v: string) => {
    local.onUpdateValue?.(v);
  };

  const onInput: JSX.EventHandlerUnion<HTMLTextAreaElement, InputEvent> = (e) => {
    let v = e.currentTarget.value;
    v = clampByMaxLength(v, effectiveMax());
    if (v !== e.currentTarget.value) {
      e.currentTarget.value = v;
    }
    setLive(v);

    const wait = local.debounceMs ?? 0;
    if (wait <= 0) {
      emitUpdate(v);
      return;
    }
    cancelDebounce();
    debounceTimer = setTimeout(() => {
      debounceTimer = undefined;
      emitUpdate(v);
    }, wait);
  };

  const handleClear = () => {
    if (local.disabled || local.readonly) return;
    cancelDebounce();
    local.onClear?.();
    setLive('');
    emitUpdate('');
  };

  const showClearBtn = () =>
    local.showClear && !local.disabled && !local.readonly && charCount(live()) > 0;

  const showCounter = () => local.showCount && effectiveMax() > 0;

  /** 全局 base.css 给 textarea 写了 height:36px，会压掉 rows，必须用 auto 交还给浏览器按行数计算 */
  const fieldStyle = (): JSX.CSSProperties => {
    const needClearPad = showClearBtn();
    const needCountPad = showCounter();
    return {
      'box-sizing': 'border-box',
      width: '100%',
      height: 'auto',
      'min-height': 0,
      resize: 'vertical',
      'padding-top': '8px',
      'padding-left': '8px',
      'padding-right': needClearPad ? '44px' : '8px',
      'padding-bottom': needCountPad ? '26px' : '8px',
      ...local.textareaStyle,
    };
  };


  const clearBtnStyle: JSX.CSSProperties = {
    position: 'absolute',
    top: '50%',
    right: '8px',
    transform: 'translateY(-50%)',
    'z-index': 100,
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    'border-radius': '50%',
    cursor: 'pointer',
  };

  return (
    <div class={`difi-textarea ${local.wrapperClass ?? ''} ${local.class ?? ''}`.trim()} style={local.style}>
      <div class="difi-textarea__inner width-100" style={innerBoxStyle}>
        <textarea
          {...textareaRest}
          class={`difi-textarea__field width-100 f-input ${local.textareaClass ?? ''}`.trim()}
          style={fieldStyle()}
          rows={local.rows}
          placeholder={local.placeholder}
          disabled={local.disabled}
          readonly={local.readonly}
          value={live()}
          onInput={onInput}
        />

        {showClearBtn() ? (
          <Icon
            name="common-delete-solid"
            class="icon-s-S"
            style={{
              width: '16px',
              height: '16px',
              ...clearBtnStyle,
              // 实心圆灰底 + 白色叉（对应 color-XL / 白）；勿用 icon-c 同色 fill/stroke
              fill: '#C2C3CB',
              stroke: '#FFFFFF',
            }}
            onclick={handleClear}
          />
        ) : null}

        {showCounter() ? (
          <div class="difi-textarea__count font-s-XS color-L" style={countStyle}>
            {charCount(live())}/{effectiveMax()}
          </div>
        ) : null}
      </div>
    </div>
  );
};
